import { atomWithStorage, createJSONStorage } from "jotai/utils";
import { z } from "zod";

type StringOrNull = string | null;

// this is the validation for the partner in the analytics
// it should match the type `Partner` in `packages/utils/partner-provider.tsx`
// and more that have no co-branded experience
const partnerSchema = z
  .enum([
    // Original colors
    "nesto",
    // Partners with co-branded
    "canadalife",
    "dwm",
    "eqb",
    "ferique",
    "fhg",
    "ia",
    "optimize",
    "quadrus",
    // next partners doesn't have co-branded
    "IG_WM",
    "TD",
    "Tangerine",
    "assetdirect",
    "billdr",
    "borrowell",
    "brookfield",
    "caut",
    "cbns",
    "chrisallard",
    "clearscore",
    "clicassure",
    "communities",
    "comparemortgages",
    "coopdestination",
    "coophydro",
    "creditKarma",
    "csbq",
    "door2door",
    "dwelly",
    "educfinance",
    "fido",
    "forbesadvisor",
    "greatWestLife",
    "guideHabitation",
    "hardbacon",
    "helenelauzier",
    "hellosafe",
    "home",
    "independents",
    "ipcc",
    "ipc_pinnacle",
    "justo",
    "laurentien",
    "lenderforall",
    "loanscanada",
    "lpfinancial",
    "manulife",
    "milesopedia",
    "moneydotca",
    "moneygenius",
    "moneywise",
    "movemeto",
    "movingwaldo",
    "mtlguidecondo",
    "myChoice",
    "myabode",
    "mylo",
    "nerdwallet",
    "nestoreferral",
    "planhub",
    "point2homes",
    "portfoliostrategies",
    "pretsquebec",
    "properly",
    "proprioDirect",
    "psc",
    "realtor",
    "redflagdeals",
    "rentals",
    "rentfund",
    "rew",
    "rogersbank",
    "rogersbank",
    "royalLepage",
    "santasellshouses",
    "smarterloans",
    "sonnet",
    "squareone",
    "suttonSummit",
    "tlg",
    "uni",
    "unreserved",
    "viaCapitale",
    "vistoo",
    "wahi",
    "wahi_realtor",
    "wealthSimple",
    "wealthrocket",
    "worldsource",
    "zolo",
  ])
  .default("nesto")
  .catch("nesto");

export type Partner = z.infer<typeof partnerSchema>;

// NOTE: every field here is fed straight from `router.query`, so it must be
// total: any value a caller can put in the URL has to resolve to a default
// rather than throw. `useTypedRouter` parses this during SSR inside a provider
// that wraps every page, so a throw here is a 500 on the whole render.
export const analyticsSchema = z
  .object({
    clickId: z.string().optional().catch(undefined),
    ext_pid: z.string().optional().catch(undefined),
    partner: partnerSchema,
    // `.default()` only covers a missing param; `.catch()` is what covers a
    // present-but-unparseable one. `subPartnerId` is forwarded to the BE in
    // the create-account payload, hence the int/non-negative bounds too.
    subPartnerId: z.coerce.number().int().nonnegative().default(0).catch(0),
  })
  .transform(({ clickId, ext_pid, partner, subPartnerId }) => ({
    // renamed marketing team query params to internal name
    affiliateMarketingId: clickId,
    impressionsTrackingId: ext_pid,
    partner,
    subPartnerId,
  }));

export type Analytics = z.infer<typeof analyticsSchema>;

const storage = createJSONStorage<StringOrNull>(() => sessionStorage);
const storagePartner = createJSONStorage<Partner>(() => sessionStorage);
const storagesubPartnerId = createJSONStorage<number>(() => sessionStorage);

const STORAGE_PREFIX = "n:";

const defaultValue: StringOrNull = null;
const partnerDefaultValue: Partner = "nesto";
const subPartnerIdDefaultValue = 0;

export const affiliateMarketingIdAtom = atomWithStorage<StringOrNull>(
  `${STORAGE_PREFIX}affiliateMarketingId`,
  defaultValue,
  storage
);

export const impressionsTrackingIdAtom = atomWithStorage<StringOrNull>(
  `${STORAGE_PREFIX}impressionsTrackingId`,
  defaultValue,
  storage
);

export const partnerAtom = atomWithStorage(
  `${STORAGE_PREFIX}partner`,
  partnerDefaultValue,
  storagePartner
);

export const subPartnerIdAtom = atomWithStorage(
  `${STORAGE_PREFIX}subPartnerId`,
  subPartnerIdDefaultValue,
  storagesubPartnerId
);
